import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  normalizeProtocol,
  presetDefaults,
  type ScaleBrandPreset,
  type ScaleProtocol,
} from '@/utils/scaleProtocols';
import {
  DEFAULT_SCALE_BARCODE_PROFILE,
  type ScaleBarcodeProfile,
  type WeightInputMethod,
} from '@/utils/scaleBarcode';

export type ScaleConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface ScaleSavedPort {
  id: string;
  label: string;
  /** Electron COM path */
  path?: string;
  usbVendorId?: number;
  usbProductId?: number;
}

export interface ScaleSettings {
  brandPreset: ScaleBrandPreset;
  protocol: ScaleProtocol;
  /** Electron serial path e.g. COM3 /dev/ttyUSB0 */
  path: string;
  /** Web Serial remembered port id (usb-VID-PID or index) */
  portId: string | null;
  baudRate: number;
  dataBits: 7 | 8;
  stopBits: 1 | 2;
  parity: 'none' | 'even' | 'odd';
  autoConnect: boolean;
  /** Only accept readings marked stable when protocol provides status */
  stableOnly: boolean;
  /** How the POS accepts weight for sell-by-weight items */
  weightInputMethod: WeightInputMethod;
  scaleBarcodeProfile: ScaleBarcodeProfile;
}

interface ScaleState {
  settings: ScaleSettings;
  ports: ScaleSavedPort[];
  status: ScaleConnectionStatus;
  errorMsg: string | null;
  isDirty: boolean;
  draft: ScaleSettings;
  lastWeightKg: number | null;

  updateDraft: <K extends keyof ScaleSettings>(key: K, value: ScaleSettings[K]) => void;
  updateDraftProfile: <K extends keyof ScaleBarcodeProfile>(key: K, value: ScaleBarcodeProfile[K]) => void;
  applyBrandPreset: (preset: ScaleBrandPreset) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  resetDefaults: () => void;
  setPorts: (ports: ScaleSavedPort[]) => void;
  addPort: (port: ScaleSavedPort) => void;
  setStatus: (status: ScaleConnectionStatus) => void;
  setError: (msg: string | null) => void;
  setLastWeightKg: (kg: number | null) => void;
}

export const DEFAULT_SCALE_SETTINGS: ScaleSettings = {
  brandPreset: 'none',
  protocol: 'generic_continuous',
  path: '',
  portId: null,
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: 'none',
  autoConnect: true,
  stableOnly: false,
  weightInputMethod: 'live',
  scaleBarcodeProfile: { ...DEFAULT_SCALE_BARCODE_PROFILE },
};

const OLD_KEY = 'pos_scale_settings';

function migrateLegacyLocalStorage(): Partial<ScaleSettings> | null {
  try {
    const raw = localStorage.getItem(OLD_KEY);
    if (!raw) return null;
    const old = JSON.parse(raw) as { path?: string; baudRate?: number; protocol?: string };
    return {
      path: old.path || '',
      baudRate: old.baudRate || 9600,
      protocol: normalizeProtocol(old.protocol),
      brandPreset: 'generic',
    };
  } catch {
    return null;
  }
}

function normalizePersistedSettings(
  partial: Partial<ScaleSettings> | undefined,
  opts?: { upgradingExisting?: boolean },
): ScaleSettings {
  const base = { ...DEFAULT_SCALE_SETTINGS, ...(partial || {}) };
  base.protocol = normalizeProtocol(base.protocol);
  if (!base.brandPreset) base.brandPreset = 'generic';
  base.scaleBarcodeProfile = {
    ...DEFAULT_SCALE_BARCODE_PROFILE,
    ...(partial?.scaleBarcodeProfile || {}),
  };
  if (!partial || partial.weightInputMethod == null) {
    // Existing saved settings without the new key → keep printed PLU working
    base.weightInputMethod = opts?.upgradingExisting ? 'both' : DEFAULT_SCALE_SETTINGS.weightInputMethod;
  } else if (!['live', 'barcode', 'both'].includes(partial.weightInputMethod)) {
    base.weightInputMethod = DEFAULT_SCALE_SETTINGS.weightInputMethod;
  }
  return base;
}

export const useScaleStore = create<ScaleState>()(
  persist(
    (set) => ({
      settings: DEFAULT_SCALE_SETTINGS,
      ports: [],
      status: 'idle',
      errorMsg: null,
      isDirty: false,
      draft: DEFAULT_SCALE_SETTINGS,
      lastWeightKg: null,

      updateDraft: (key, value) =>
        set((s) => ({ draft: { ...s.draft, [key]: value }, isDirty: true })),

      updateDraftProfile: (key, value) =>
        set((s) => ({
          draft: {
            ...s.draft,
            scaleBarcodeProfile: { ...s.draft.scaleBarcodeProfile, [key]: value },
          },
          isDirty: true,
        })),

      applyBrandPreset: (preset) =>
        set((s) => {
          const d = presetDefaults(preset);
          return {
            draft: {
              ...s.draft,
              brandPreset: preset,
              protocol: d.protocol,
              baudRate: d.baudRate,
            },
            isDirty: true,
          };
        }),

      saveDraft: () =>
        set((s) => {
          try {
            localStorage.removeItem(OLD_KEY);
          } catch {
            /* ignore */
          }
          return { settings: { ...s.draft, scaleBarcodeProfile: { ...s.draft.scaleBarcodeProfile } }, isDirty: false };
        }),

      discardDraft: () =>
        set((s) => ({ draft: { ...s.settings, scaleBarcodeProfile: { ...s.settings.scaleBarcodeProfile } }, isDirty: false })),

      resetDefaults: () =>
        set({
          draft: {
            ...DEFAULT_SCALE_SETTINGS,
            scaleBarcodeProfile: { ...DEFAULT_SCALE_BARCODE_PROFILE },
          },
          isDirty: true,
        }),

      setPorts: (ports) => set({ ports }),
      addPort: (port) =>
        set((s) => ({
          ports: s.ports.find((p) => p.id === port.id) ? s.ports : [...s.ports, port],
        })),

      setStatus: (status) => set({ status }),
      setError: (errorMsg) => set({ errorMsg }),
      setLastWeightKg: (lastWeightKg) => set({ lastWeightKg }),
    }),
    {
      name: 'scale-settings',
      partialize: (s) => ({ settings: s.settings, ports: s.ports }),
      merge: (persisted: unknown, current) => {
        const p = persisted as { settings?: Partial<ScaleSettings>; ports?: ScaleSavedPort[] } | null;
        let upgradingExisting = !!(p?.settings);
        let partial = p?.settings;
        if (!partial) {
          const legacy = migrateLegacyLocalStorage();
          if (legacy) {
            partial = legacy;
            upgradingExisting = true;
          }
        }
        const base = normalizePersistedSettings(partial, { upgradingExisting });
        return {
          ...current,
          settings: base,
          ports: p?.ports ?? [],
          draft: { ...base, scaleBarcodeProfile: { ...base.scaleBarcodeProfile } },
        };
      },
    },
  ),
);

export function getActiveScaleSettings(): ScaleSettings {
  return useScaleStore.getState().settings;
}

export function isLiveScaleEnabled(settings?: ScaleSettings): boolean {
  const s = settings || useScaleStore.getState().settings;
  return s.weightInputMethod === 'live' || s.weightInputMethod === 'both';
}

export function isScaleBarcodeEnabled(settings?: ScaleSettings): boolean {
  const s = settings || useScaleStore.getState().settings;
  return s.weightInputMethod === 'barcode' || s.weightInputMethod === 'both';
}

export function isScaleConfigured(settings?: ScaleSettings): boolean {
  const s = settings || useScaleStore.getState().settings;
  if (!isLiveScaleEnabled(s)) return false;
  if (s.brandPreset === 'none' && !s.path && !s.portId) return false;
  if (s.protocol === 'keyboard_wedge') return true;
  return !!(s.path || s.portId);
}
